import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandInquiryForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    contact_name: '',
    brand_name: '',
    email: '',
    website: '',
    role: '',
    region: '',
    message: '',
  });
  const [consent, setConsent] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const canSubmit =
    consent &&
    form.contact_name.trim().length > 0 &&
    form.brand_name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.message.trim().length > 0 &&
    status !== 'loading';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/brand-inquiry`, {
        ...form,
        contact_name: form.contact_name.trim(),
        brand_name: form.brand_name.trim(),
        email: form.email.trim(),
        website: form.website.trim() || null,
        role: form.role.trim() || null,
        region: form.region.trim() || null,
        message: form.message.trim(),
        consent_given: true,
      });
      setStatus('success');
      setForm({
        contact_name: '',
        brand_name: '',
        email: '',
        website: '',
        role: '',
        region: '',
        message: '',
      });
      setConsent(false);
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          t(
            'Er ging iets mis. Probeer het later opnieuw.',
            'Something went wrong. Please try again later.'
          )
      );
    }
  };

  const labelClass = 'block text-sm font-medium text-gray-800 mb-1.5';
  const inputClass =
    'w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow';

  if (status === 'success') {
    return (
      <div
        className="bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-10 text-center"
        data-testid="brand-inquiry-success"
      >
        <h3 className="text-2xl font-light text-gray-900 mb-3">
          {t('Bedankt voor je bericht.', 'Thanks for reaching out.')}
        </h3>
        <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
          {t(
            'We hebben je aanvraag goed ontvangen en nemen binnen enkele werkdagen contact met je op.',
            "We've received your inquiry and will get back to you within a few business days."
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 sm:p-8 space-y-5"
      data-testid="brand-inquiry-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass} htmlFor="bi-contact-name">
            {t('Jouw naam', 'Your name')} <span className="text-pink-500">*</span>
          </label>
          <input
            id="bi-contact-name"
            type="text"
            required
            value={form.contact_name}
            onChange={onChange('contact_name')}
            className={inputClass}
            data-testid="brand-inquiry-contact-name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="bi-brand-name">
            {t('Merknaam', 'Brand name')} <span className="text-pink-500">*</span>
          </label>
          <input
            id="bi-brand-name"
            type="text"
            required
            value={form.brand_name}
            onChange={onChange('brand_name')}
            className={inputClass}
            data-testid="brand-inquiry-brand-name"
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="bi-email">
          {t('E-mailadres', 'Email')} <span className="text-pink-500">*</span>
        </label>
        <input
          id="bi-email"
          type="email"
          required
          value={form.email}
          onChange={onChange('email')}
          className={inputClass}
          data-testid="brand-inquiry-email"
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="bi-message">
          {t('Vertel ons over je merk', 'Tell us about your brand')} <span className="text-pink-500">*</span>
        </label>
        <textarea
          id="bi-message"
          required
          rows={4}
          value={form.message}
          onChange={onChange('message')}
          placeholder={t(
            'Wat maakt jouw merk uniek? Welke collecties wil je onder de aandacht brengen?',
            'What makes your brand unique? Which collections would you like to highlight?'
          )}
          className={inputClass}
          data-testid="brand-inquiry-message"
        />
      </div>

      {/* Optional collapsible section */}
      <div className="border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => setShowOptional((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          data-testid="brand-inquiry-toggle-optional"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${showOptional ? 'rotate-180' : ''}`} />
          {t('Aanvullende info (optioneel)', 'Additional info (optional)')}
        </button>
        {showOptional && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-5" data-testid="brand-inquiry-optional-fields">
            <div>
              <label className={labelClass} htmlFor="bi-website">
                {t('Website', 'Website')}
              </label>
              <input
                id="bi-website"
                type="url"
                placeholder="https://"
                value={form.website}
                onChange={onChange('website')}
                className={inputClass}
                data-testid="brand-inquiry-website"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="bi-role">
                {t('Functie', 'Role')}
              </label>
              <input
                id="bi-role"
                type="text"
                placeholder={t('bv. Marketing Manager', 'e.g. Marketing Manager')}
                value={form.role}
                onChange={onChange('role')}
                className={inputClass}
                data-testid="brand-inquiry-role"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="bi-region">
                {t('Regio', 'Region')}
              </label>
              <input
                id="bi-region"
                type="text"
                placeholder={t('bv. Benelux', 'e.g. Benelux')}
                value={form.region}
                onChange={onChange('region')}
                className={inputClass}
                data-testid="brand-inquiry-region"
              />
            </div>
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
          data-testid="brand-inquiry-consent"
        />
        <span>
          {t(
            'Ik ga akkoord met de verwerking van mijn gegevens om mijn aanvraag te beantwoorden, zoals beschreven in het ',
            'I agree to the processing of my data to respond to my inquiry, as described in the '
          )}
          <Link to="/privacy" className="text-purple-600 underline hover:text-purple-800">
            {t('privacybeleid', 'privacy policy')}
          </Link>
          .
        </span>
      </label>

      <div className="pt-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full sm:w-auto bg-gray-900 text-white px-8 py-3.5 rounded-full font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="brand-inquiry-submit"
        >
          {status === 'loading'
            ? t('Bezig met versturen…', 'Sending…')
            : t('Laten we praten', "Let's talk")}
        </button>
        {status === 'error' && (
          <p className="text-sm text-red-600 mt-3" data-testid="brand-inquiry-error">
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
};

export default BrandInquiryForm;
